// ─────────────────────────────────────────────────────────────────────────
// markdownLoader.js — shared utility for lazy-loading markdown via Vite globs
//
// Background (Session 34):
// Routes use React.lazy to split the page bundle. The markdown files
// (articles, blog, states, guides) used to be eager-globbed inside each
// page module, which meant rollup bundled ALL files for a content type
// into whichever chunk first imported them — producing 1.35 MB "wyoming"
// chunks that defeated the point of route-splitting.
//
// The fix: glob with `eager: false`, so Vite emits one async chunk per
// markdown file. Pages then await only the file they need.
//
// Two helpers:
//   • loadMarkdownBySlug(modules, slug, parseFrontmatter)
//     — for detail pages (ArticlePage, BlogPost, StatePage, GuidePage).
//       Uses a filename heuristic to fetch the most likely file first,
//       falling back to a full scan only if the heuristic misses.
//
//   • loadAllMarkdown(modules, parseFrontmatter)
//     — for index pages (ArticleIndex, BlogIndex) and ContributorPage,
//       which must scan every entry to build a listing or
//       contributor-attribution view. Promise.all parallel fetch.
// ─────────────────────────────────────────────────────────────────────────

// Extract a slug-like hint from a markdown path.
// e.g. "../articles/03-vinegar-question.md" → "vinegar-question"
//      "../states/california.md"            → "california"
function pathSlugHint(path) {
  const file = path.split('/').pop().replace(/\.md$/, '').replace(/^\d+-/, '');
  return file;
}

// Load a single markdown file matching the given frontmatter slug.
// Tries probable candidates first based on filename heuristic; falls back
// to scanning all files if the heuristic misses (e.g., article 03 where
// the filename slug is a prefix of the frontmatter slug).
//
// `modules` is the result of `import.meta.glob(..., { eager: false })`,
// shaped as { path: () => Promise<rawString> }.
export async function loadMarkdownBySlug(modules, slug, parseFrontmatter) {
  if (!slug) return null;
  const entries = Object.entries(modules);

  // Pass 1 — probable candidates by filename heuristic.
  // Match if the slug equals the path hint, or one is a prefix of the other
  // (handles cases like filename "vinegar-question" → slug "vinegar-question-when-it-works").
  const probable = entries.filter(([path]) => {
    const hint = pathSlugHint(path);
    return slug === hint || slug.startsWith(hint + '-') || hint.startsWith(slug + '-');
  });

  for (const [path, loader] of probable) {
    const raw = await loader();
    const parsed = parseFrontmatter(raw);
    const fm = parsed.frontmatter || parsed; // tolerate parsers returning the object directly
    if (fm && fm.slug === slug) {
      return { ...parsed, path };
    }
  }

  // Pass 2 — full scan fallback for edge cases where the heuristic misses.
  const seen = new Set(probable.map(([p]) => p));
  for (const [path, loader] of entries) {
    if (seen.has(path)) continue;
    const raw = await loader();
    const parsed = parseFrontmatter(raw);
    const fm = parsed.frontmatter || parsed;
    if (fm && fm.slug === slug) {
      return { ...parsed, path };
    }
  }

  return null;
}

// Load all markdown files in a glob in parallel.
// Returns an array of { path, frontmatter, body } objects.
// Used by index pages that need to display every entry.
// Tolerates parsers that return either { frontmatter, body } or a bare
// frontmatter object directly — normalizes both into a consistent shape.
export async function loadAllMarkdown(modules, parseFrontmatter) {
  const entries = Object.entries(modules);
  const results = await Promise.all(
    entries.map(async ([path, loader]) => {
      const raw = await loader();
      const parsed = parseFrontmatter(raw);
      const frontmatter = parsed.frontmatter || parsed;
      const body = parsed.body || '';
      return { path, frontmatter, body };
    })
  );
  return results;
}
